import React, { useState, useEffect, useCallback, useRef } from 'react';
import api from '../../utils/api';
import { jsPDF } from 'jspdf';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../context/ToastContext';
import { useConfirm } from '../../context/ConfirmContext';
import { getErrorMessage, formatRupee, formatDate, parseNum } from '../../utils/errorHandler';
import Pagination from '../common/Pagination';
import Card from '../common/Card';
import Button from '../common/Button';
import PageHeader from '../common/PageHeader';
import { TableSkeleton } from '../common/Skeleton';

const DailyBusinessPage = ({ navigationParams, onNavigationComplete }) => {
  const { hasPermission } = useAuth();
  const toast = useToast();
  const confirm = useConfirm();

  // Form state
  const [showForm, setShowForm] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [viewingRecord, setViewingRecord] = useState(null);
  const [formData, setFormData] = useState({
    business_date: new Date().toISOString().split('T')[0],
    opening_balance: '',
    cash: '',
    gpay: '',
    debit_card: '',
    credit_card: '',
    excise_person: '',
    police_person: '',
    food: '',
    salary_payment: '',
    other_expenses: '',
    cash_received: ''
  });
  const initialExpenseRows = [
    { reason: 'Salary Payment', amount: '' },
    { reason: 'Food Expense', amount: '' }
  ];
  const [expenses, setExpenses] = useState(initialExpenseRows);

  // Records state
  const [records, setRecords] = useState([]);
  const [filteredRecords, setFilteredRecords] = useState([]);
  const [loading, setLoading] = useState(false);
  const [rateLimited, setRateLimited] = useState(false);
  const retryCountRef = useRef(0);
  const loadingRef = useRef(false); // Prevent concurrent calls
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 5;

  // Filter state
  const [showFilters, setShowFilters] = useState(false);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [searchTerm, setSearchTerm] = useState('');

  // Summary state
  const [summary, setSummary] = useState({
    totalIncome: 0,
    totalExpenses: 0,
    netProfit: 0,
    closingBalance: 0
  });

  const [openingBalanceMeta, setOpeningBalanceMeta] = useState({
    autoApplied: false,
    sourceDate: null,
    loading: false,
    error: null
  });

  // Track latest date for consecutive date restriction
  const [latestDate, setLatestDate] = useState(null);

  // Load records on mount
  useEffect(() => {
    // Load all records on mount - client-side filtering will handle date filters
    // This avoids multiple API calls when navigating from dashboard
    if (!loadingRef.current) {
      loadRecords();
    }
  }, []); // Only run once on mount

  // Handle navigation parameters from dashboard
  useEffect(() => {
    if (navigationParams?.filterDate) {
      // Set date filters - client-side filtering will handle showing the right records
      const filterDate = navigationParams.filterDate;
      setStartDate(filterDate);
      setEndDate(filterDate);
      // If records are already loaded, filters will apply automatically
      // If not loaded yet, wait for loadRecords to complete then filters will apply
    }
  }, [navigationParams?.filterDate]); // Only update filters if filterDate changes

  // Handle record highlighting from navigation params
  useEffect(() => {
    if (navigationParams?.recordId) {
      // Wait for records to load, then highlight
      const timer = setTimeout(() => {
        const recordElement = document.getElementById(`record-${navigationParams.recordId}`);
        if (recordElement) {
          recordElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
          recordElement.classList.add('ring-2', 'ring-primary', 'ring-offset-2');
          setTimeout(() => {
            recordElement.classList.remove('ring-2', 'ring-primary', 'ring-offset-2');
          }, 3000);
        }
        // Clear navigation params after processing
        if (onNavigationComplete) {
          setTimeout(() => onNavigationComplete(), 1000);
        }
      }, 1000); // Wait 1 second for records to load
      
      return () => clearTimeout(timer);
    } else if (navigationParams && onNavigationComplete) {
      // Clear params if no recordId
      const timer = setTimeout(() => onNavigationComplete(), 500);
      return () => clearTimeout(timer);
    }
  }, [navigationParams?.recordId, filteredRecords.length]); // Wait for records to be filtered

  // Apply filters when they change
  useEffect(() => {
    applyFilters();
    setCurrentPage(1); // Reset to first page when filters change
  }, [records, startDate, endDate, searchTerm]);

  useEffect(() => {
    setSummary(calculateSummary(filteredRecords));
  }, [filteredRecords]);

  // Pagination calculations
  const totalPages = Math.ceil(filteredRecords.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedRecords = filteredRecords.slice(startIndex, endIndex);

  const fetchOpeningBalance = useCallback(async (date) => {
    if (!date || editingRecord) {
      setOpeningBalanceMeta(prev => ({
        autoApplied: false,
        sourceDate: null,
        loading: false,
        error: null
      }));
      return;
    }

    try {
      setOpeningBalanceMeta(prev => ({
        ...prev,
        loading: true,
        error: null
      }));

      const response = await api.get('/daily-business/opening-balance', {
        params: { business_date: date }
      });

      const { opening_balance, source_date } = response.data || {};

      if (opening_balance === null || opening_balance === undefined) {
        setFormData(prev => ({
          ...prev,
          opening_balance: ''
        }));
        setOpeningBalanceMeta({
          autoApplied: false,
          sourceDate: null,
          loading: false,
          error: null
        });
        return;
      }

      setFormData(prev => ({
        ...prev,
        opening_balance: Math.round(Number(opening_balance)).toString()
      }));
      setOpeningBalanceMeta({
        autoApplied: true,
        sourceDate: source_date,
        loading: false,
        error: null
      });
    } catch (error) {
      const errorMessage = getErrorMessage(error);
      setOpeningBalanceMeta({
        autoApplied: false,
        sourceDate: null,
        loading: false,
        error: errorMessage
      });
    }
  }, [editingRecord]);

  useEffect(() => {
    if (showForm && !editingRecord) {
      fetchOpeningBalance(formData.business_date);
    }
  }, [showForm, editingRecord, formData.business_date, fetchOpeningBalance]);

  const loadRecords = async (startDateParam = null, endDateParam = null) => {
    // Prevent concurrent calls
    if (loadingRef.current) {
      return;
    }

    try {
      loadingRef.current = true;
      setLoading(true);
      
      // Build query parameters
      const params = {};
      if (startDateParam) {
        params.startDate = startDateParam;
      }
      if (endDateParam) {
        params.endDate = endDateParam;
      }
      
      const response = await api.get('/daily-business', {
        params: params
      });
      
      if (response.data && response.data.records) {
        const sortedRecords = response.data.records.sort((a, b) => 
          new Date(b.business_date) - new Date(a.business_date)
        );
        setRecords(sortedRecords);
        setFilteredRecords(sortedRecords);
        
        // Find latest date for consecutive date restriction
        if (sortedRecords.length > 0) {
          const dates = sortedRecords.map(r => r.business_date).sort();
          setLatestDate(dates[dates.length - 1]); // Latest date (last in sorted array)
        } else {
          setLatestDate(null);
        }
        
        // Reset retry count and rate limit flag on successful load
        retryCountRef.current = 0;
        setRateLimited(false);
      } else {
        // No records found
        setRecords([]);
        setFilteredRecords([]);
        setLatestDate(null);
        // Reset retry count even if no records found
        retryCountRef.current = 0;
      }
    } catch (error) {
      console.error('Error loading records:', error);
      const errorMessage = getErrorMessage(error);
      
      // Handle 429 rate limit errors - DO NOT RETRY automatically
      // This prevents making the rate limit problem worse
      if (error.response?.status === 429) {
        console.error('Rate limit exceeded. Please wait a few minutes before trying again.');
        console.error('The server allows 100 requests per 15 minutes. Please reduce the number of requests.');
        setRateLimited(true);
        // Auto-clear rate limit message after 15 minutes
        setTimeout(() => {
          setRateLimited(false);
        }, 15 * 60 * 1000);
        retryCountRef.current = 0;
      } else {
        // Reset retry count and rate limit flag on other errors
        retryCountRef.current = 0;
        setRateLimited(false);
      }
      
      console.error('Load records error:', errorMessage);
      // Don't show alert for load errors, just log them
    } finally {
      setLoading(false);
      loadingRef.current = false;
    }
  };

  const calculateSummary = (data = []) => {
    return data.reduce((acc, record) => {
      // Business income without opening balance
      const businessIncome = (parseFloat(record.cash || 0) + parseFloat(record.gpay || 0) + 
                              parseFloat(record.debit_card || 0) + parseFloat(record.credit_card || 0));
      acc.totalIncome += businessIncome;
      acc.totalExpenses += parseFloat(record.total_expenses) || 0;
      // Net profit from database (already calculated correctly: businessIncome - expenses)
      acc.netProfit += parseFloat(record.net_profit) || 0;
      acc.closingBalance += parseFloat(record.closing_balance) || 0;
      return acc;
    }, { totalIncome: 0, totalExpenses: 0, netProfit: 0, closingBalance: 0 });
  };

  const applyFilters = () => {
    let filtered = [...records];

    // Date filter - compare date strings directly (YYYY-MM-DD format)
    if (startDate || endDate) {
      filtered = filtered.filter(record => {
        const recordDate = record.business_date?.split('T')[0] || record.business_date;
        if (!recordDate) return false;
        
        // If both start and end dates are the same, do exact match
        if (startDate && endDate && startDate === endDate) {
          return recordDate === startDate;
        }
        
        // Otherwise, do range filtering
        if (startDate && recordDate < startDate) return false;
        if (endDate && recordDate > endDate) return false;
        
        return true;
      });
    }

    // Search filter
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(record => {
        const date = new Date(record.business_date).toLocaleDateString();
        return (
          date.toLowerCase().includes(term) ||
          formatRupee(record.opening_balance).toLowerCase().includes(term) ||
          formatRupee(record.total_income).toLowerCase().includes(term) ||
          formatRupee(record.total_expenses).toLowerCase().includes(term) ||
          formatRupee(record.net_profit).toLowerCase().includes(term) ||
          formatRupee(record.closing_balance).toLowerCase().includes(term)
        );
      });
    }

    setFilteredRecords(filtered);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    // Validate consecutive date selection for new entries (not editing)
    if (name === 'business_date' && !editingRecord && latestDate) {
      const selectedDate = new Date(value);
      const latestDateObj = new Date(latestDate);
      // Set time to start of day for comparison
      selectedDate.setHours(0, 0, 0, 0);
      latestDateObj.setHours(0, 0, 0, 0);
      
      // Calculate the next consecutive date (latest date + 1 day)
      const nextConsecutiveDate = new Date(latestDateObj);
      nextConsecutiveDate.setDate(nextConsecutiveDate.getDate() + 1);
      nextConsecutiveDate.setHours(0, 0, 0, 0);
      
      // Check if selected date is not the next consecutive date
      if (selectedDate.getTime() !== nextConsecutiveDate.getTime()) {
        const nextDateStr = nextConsecutiveDate.toISOString().split('T')[0];
        toast.error(`You must enter dates consecutively. The last entry was on ${new Date(latestDate).toLocaleDateString()}. Please select the next day: ${new Date(nextDateStr).toLocaleDateString()}`);
        // Reset to the next consecutive date
        setFormData(prev => ({
          ...prev,
          business_date: nextDateStr
        }));
        return; // Don't update with the invalid date
      }
    }
    
    // Max length 7 validation & integer only for numeric inputs
    if (e.target.type === 'number') {
      if (value.includes('.') || value.includes('e') || value.includes('E') || value.includes('-') || value.includes('+')) {
        return;
      }
      if (value.length > 7) {
        return;
      }
    }
    
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const parseExpenseDetails = (details) => {
    if (Array.isArray(details)) return details;
    if (typeof details === 'string') {
      try {
        const parsed = JSON.parse(details);
        if (Array.isArray(parsed)) return parsed;
      } catch (e) {
        return [];
      }
    }
    return [];
  };

  const sanitizeExpenseRows = (rows = []) => {
    if (!Array.isArray(rows)) return [];
    const limitReasonWords = (text = '', maxWords = 100) => {
      const words = text.toString().trim().split(/\s+/);
      return words.slice(0, maxWords).join(' ');
    };
    return rows
      .map(row => {
        const reason = limitReasonWords(row.reason || row.name || '');
        const amount = parseNum(row.amount);
        return {
          reason: reason.trim(),
          amount: Number.isFinite(amount) && amount >= 0 ? amount : 0
        };
      })
      .filter(row => row.reason || row.amount > 0);
  };

  const getExpenseTotal = (rows = []) => {
    return sanitizeExpenseRows(rows).reduce((sum, row) => sum + (parseNum(row.amount) || 0), 0);
  };

  const handleExpenseChange = (index, field, value) => {
    setExpenses(prev => {
      const next = [...prev];
      const updated = { ...(next[index] || { reason: '', amount: '' }) };
      if (field === 'reason') {
        // Allow spaces - just limit total length to prevent abuse
        updated[field] = value.toString().slice(0, 200);
      } else {
        if (value.includes('.') || value.includes('e') || value.includes('E') || value.includes('-') || value.includes('+')) {
          return prev;
        }
        if (value.length > 7) {
          return prev;
        }
        updated[field] = value;
      }
      next[index] = updated;
      return next;
    });
  };

  const addExpenseRow = () => {
    setExpenses(prev => {
      if (prev.length >= 20) return prev;
      return [...prev, { reason: '', amount: '' }];
    });
  };

  const removeExpenseRow = (index) => {
    if (index === 0 || index === 1) return; // keep Salary and Food rows
    setExpenses(prev => {
      const next = prev.filter((_, idx) => idx !== index);
      return next.length ? next : [{ reason: '', amount: '' }];
    });
  };


  const calculateTotals = () => {
    const opening = parseNum(formData.opening_balance);
    const cash = parseNum(formData.cash);
    const gpay = parseNum(formData.gpay);
    const debit = parseNum(formData.debit_card);
    const credit = parseNum(formData.credit_card);
    const expenseTotal = getExpenseTotal(expenses);
    const cashReceived = parseNum(formData.cash_received);

    const businessIncome = cash + gpay + debit + credit; // Without opening balance
    const totalIncome = opening + businessIncome;
    const totalExpenses = expenseTotal;
    // Net profit = Business Income - Expenses (excluding opening balance)
    const netProfit = businessIncome - totalExpenses;
    // Closing balance = Opening balance + Cash sales - Total Expenses
    const closing = opening + cash - totalExpenses;
    // Opening balance for next day = Closing balance - Cash received
    const nextDayOpening = closing - cashReceived;

    return {
      businessIncome,
      totalIncome,
      totalExpenses,
      netProfit,
      closing,
      nextDayOpening
    };
  };


  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const totals = calculateTotals();

      const cleanedExpenses = sanitizeExpenseRows(expenses);

      const payload = {
        business_date: formData.business_date,
        opening_balance: parseNum(formData.opening_balance),
        cash: parseNum(formData.cash),
        gpay: parseNum(formData.gpay),
        debit_card: parseNum(formData.debit_card),
        credit_card: parseNum(formData.credit_card),
        expenses: cleanedExpenses,
        other_expenses: totals.totalExpenses,
        cash_received: parseNum(formData.cash_received)
      };

      if (editingRecord) {
        await api.put(`/daily-business/${editingRecord.id}`, payload);
        toast.success('Record updated successfully!');
      } else {
        await api.post('/daily-business', payload);
        toast.success('Record created successfully!');
      }

      resetForm();
      loadRecords();
    } catch (error) {
      console.error('Error submitting:', error);
      const errorMessage = getErrorMessage(error);
      toast.error(errorMessage);
    }
  };

  const resetForm = () => {
    // Set default date to next consecutive date if latestDate exists, otherwise today
    let defaultDate = new Date().toISOString().split('T')[0];
    if (latestDate) {
      const nextDate = new Date(latestDate);
      nextDate.setDate(nextDate.getDate() + 1);
      defaultDate = nextDate.toISOString().split('T')[0];
    }
    
    setFormData({
      business_date: defaultDate,
      opening_balance: '',
      cash: '',
      gpay: '',
      debit_card: '',
      credit_card: '',
      excise_person: '',
      police_person: '',
      food: '',
      salary_payment: '',
      other_expenses: '',
      cash_received: ''
    });
    setExpenses(initialExpenseRows);
    setEditingRecord(null);
    setShowForm(false);
    setOpeningBalanceMeta({
      autoApplied: false,
      sourceDate: null,
      loading: false,
      error: null
    });
  };

  const buildExpensesFromRecord = (record) => {
    if (!record) return initialExpenseRows;
    const existing = parseExpenseDetails(record.other_expenses_details);
    const mapped = existing.map(item => ({
      reason: item.reason || item.name || '',
      amount: item.amount !== undefined && item.amount !== null && item.amount !== '' ? Math.round(Number(item.amount)).toString() : ''
    }));

    const ensureMandatory = (list) => {
      const hasSalary = list.some(item => (item.reason || '').toLowerCase() === 'salary payment');
      const hasFood = list.some(item => (item.reason || '').toLowerCase() === 'food expense');
      const next = [...list];
      if (!hasSalary) next.unshift({ reason: 'Salary Payment', amount: '' });
      if (!hasFood) next.unshift({ reason: 'Food Expense', amount: '' });
      return next;
    };

    if (mapped.length) {
      return ensureMandatory(mapped);
    }

    const legacy = [
      { reason: 'Excise Person', amount: record.excise_person !== undefined && record.excise_person !== null && record.excise_person !== '' ? Math.round(Number(record.excise_person)).toString() : '' },
      { reason: 'Police Person', amount: record.police_person !== undefined && record.police_person !== null && record.police_person !== '' ? Math.round(Number(record.police_person)).toString() : '' },
      { reason: 'Food', amount: record.food !== undefined && record.food !== null && record.food !== '' ? Math.round(Number(record.food)).toString() : '' },
      { reason: 'Salary Payment', amount: record.salary_payment !== undefined && record.salary_payment !== null && record.salary_payment !== '' ? Math.round(Number(record.salary_payment)).toString() : '' },
      { reason: 'Other Expenses', amount: record.other_expenses !== undefined && record.other_expenses !== null && record.other_expenses !== '' ? Math.round(Number(record.other_expenses)).toString() : '' }
    ].filter(item => parseNum(item.amount) > 0);

    return ensureMandatory(legacy.length ? legacy : []);
  };

  const handleEdit = (record) => {
    setFormData({
      business_date: record.business_date,
      opening_balance: record.opening_balance !== undefined && record.opening_balance !== null && record.opening_balance !== '' ? Math.round(Number(record.opening_balance)).toString() : '',
      cash: record.cash !== undefined && record.cash !== null && record.cash !== '' ? Math.round(Number(record.cash)).toString() : '',
      gpay: record.gpay !== undefined && record.gpay !== null && record.gpay !== '' ? Math.round(Number(record.gpay)).toString() : '',
      debit_card: record.debit_card !== undefined && record.debit_card !== null && record.debit_card !== '' ? Math.round(Number(record.debit_card)).toString() : '',
      credit_card: record.credit_card !== undefined && record.credit_card !== null && record.credit_card !== '' ? Math.round(Number(record.credit_card)).toString() : '',
      excise_person: record.excise_person !== undefined && record.excise_person !== null && record.excise_person !== '' ? Math.round(Number(record.excise_person)).toString() : '',
      police_person: record.police_person !== undefined && record.police_person !== null && record.police_person !== '' ? Math.round(Number(record.police_person)).toString() : '',
      food: record.food !== undefined && record.food !== null && record.food !== '' ? Math.round(Number(record.food)).toString() : '',
      salary_payment: record.salary_payment !== undefined && record.salary_payment !== null && record.salary_payment !== '' ? Math.round(Number(record.salary_payment)).toString() : '',
      other_expenses: record.other_expenses !== undefined && record.other_expenses !== null && record.other_expenses !== '' ? Math.round(Number(record.other_expenses)).toString() : '',
      cash_received: record.cash_received !== undefined && record.cash_received !== null && record.cash_received !== '' ? Math.round(Number(record.cash_received)).toString() : ''
    });
    setExpenses(buildExpensesFromRecord(record));
    setEditingRecord(record);
    setShowForm(true);
    setOpeningBalanceMeta({
      autoApplied: false,
      sourceDate: null,
      loading: false,
      error: null
    });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleDelete = async (id) => {
    const confirmed = await confirm({
      title: 'Delete this record?',
      message: 'This action cannot be undone.',
      confirmLabel: 'Delete',
      variant: 'danger'
    });
    if (!confirmed) return;

    try {
      await api.delete(`/daily-business/${id}`);
      toast.success('Record deleted successfully!');
      loadRecords();
    } catch (error) {
      console.error('Error deleting:', error);
      const errorMessage = getErrorMessage(error);
      toast.error(errorMessage);
    }
  };

  const handleView = (record) => {
    setViewingRecord(record);
  };

  const closeViewModal = () => {
    setViewingRecord(null);
  };

  const formatAmountForPdf = (value) => {
    return `INR ${Math.round(Number(value || 0)).toLocaleString('en-IN')}`;
  };

  // Prevent wheel and arrow key events on number inputs
  const handleNumberInputWheel = (e) => {
    e.target.blur();
  };

  const handleNumberInputKeyDown = (e) => {
    if (['ArrowUp', 'ArrowDown', '.', 'e', 'E', '+', '-'].includes(e.key)) {
      e.preventDefault();
    }
  };

  const generatePDF = (data, filename) => {
    const isMultiple = Array.isArray(data);
    const doc = isMultiple 
      ? new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' }) 
      : new jsPDF();
    
    doc.setProperties({
      title: 'Business Records',
      subject: 'Daily Business Records',
      author: 'BizTracker Pro',
      keywords: 'business, income, expenses, profit',
      creator: 'BizTracker Pro'
    });

    let yPos = 20;

    if (!Array.isArray(data)) {
      // Single record
      doc.setFontSize(20);
      doc.setTextColor(15, 43, 87);
      doc.text('Daily Business Record', 105, yPos, { align: 'center' });
      yPos += 15;

      doc.setFontSize(12);
      doc.setTextColor(100, 100, 100);
      doc.text(`Date: ${formatDate(data.business_date)}`, 105, yPos, { align: 'center' });
      yPos += 20;

      const tableX = 10;
      const colWidths = [120, 70];
      const rowHeight = 7;
      const totalWidth = colWidths.reduce((a, b) => a + b, 0);

      const expenseRows = (() => {
        const list = parseExpenseDetails(data.other_expenses_details);
        if (list.length) {
        const mapped = list.map(item => [
          item.reason || item.name || 'Expense',
          formatAmountForPdf(item.amount)
        ]);
          mapped.push(['Total Expenses', formatAmountForPdf(data.total_expenses || data.other_expenses)]);
          return mapped;
        }

        return [
          ['Total Expenses', formatAmountForPdf(data.total_expenses || data.other_expenses)]
        ];
      })();

      // Calculate values for PDF
      const openingBalance = parseFloat(data.opening_balance || 0);
      const cash = parseFloat(data.cash || 0);
      const gpay = parseFloat(data.gpay || 0);
      const debitCard = parseFloat(data.debit_card || 0);
      const creditCard = parseFloat(data.credit_card || 0);
      const businessIncome = cash + gpay + debitCard + creditCard;
      const totalBusinessIncome = openingBalance + businessIncome;
      const totalExpenses = parseFloat(data.total_expenses || data.other_expenses || 0);
      // Net profit = Business Income - Expenses (excluding opening balance)
      // Use stored net_profit if available, otherwise calculate it
      const netProfit = data.net_profit !== undefined ? parseFloat(data.net_profit || 0) : (businessIncome - totalExpenses);

      const sections = [
        {
          title: 'INCOME DETAILS',
          color: [220, 255, 220],
          textColor: [0, 100, 0],
          rows: [
            ['Opening Balance (cash available in store)', formatAmountForPdf(data.opening_balance)],
            ['Cash Sales', formatAmountForPdf(data.cash)],
            ['GPay Transactions', formatAmountForPdf(data.gpay)],
            ['Debit Card Payments', formatAmountForPdf(data.debit_card)],
            ['Credit Card Payments', formatAmountForPdf(data.credit_card)],
            ['Total Business Income', formatAmountForPdf(totalBusinessIncome)]
          ]
        },
        {
          title: 'EXPENSE DETAILS',
          color: [255, 220, 220],
          textColor: [150, 0, 0],
          rows: expenseRows
        },
        {
          title: 'SUMMARY',
          color: [220, 220, 255],
          textColor: [60, 60, 150],
          rows: [
            ['Net Profit', formatAmountForPdf(netProfit)],
            ['Closing Balance (Cash Available in Store)', formatAmountForPdf(data.closing_balance)],
            ...(data.cash_received !== undefined && data.cash_received !== null && data.cash_received !== '' && parseFloat(data.cash_received || 0) > 0 ? [
              ['Cash Handover', formatAmountForPdf(data.cash_received)],
              ['Opening Balance for Next Day (Closing - Cash Handover)', formatAmountForPdf((parseFloat(data.closing_balance || 0) - parseFloat(data.cash_received || 0)))]
            ] : [])
          ]
        }
      ];

      doc.setLineWidth(0.2);
      doc.setDrawColor(180, 180, 180);

      sections.forEach(section => {
        if (yPos > 270) {
          doc.addPage();
          yPos = 20;
        }

        doc.setFillColor(...section.color);
        doc.rect(tableX, yPos - 5, totalWidth, 10, 'F');
        doc.setTextColor(...section.textColor);
        doc.setFontSize(12);
        doc.setFont(undefined, 'bold');
        doc.text(section.title, 105, yPos, { align: 'center' });
        yPos += 15;

        doc.setTextColor(0, 0, 0);
        doc.setFont(undefined, 'normal');
        doc.setFontSize(10);

        let sectionY = yPos;
        doc.setFillColor(248, 250, 252);
        doc.rect(tableX, sectionY, totalWidth, rowHeight, 'F');
        doc.setFont(undefined, 'bold');
        doc.text('Description', tableX + colWidths[0] / 2, sectionY + rowHeight / 2 + 1, { align: 'center' });
        doc.text('Amount', tableX + colWidths[0] + colWidths[1] / 2, sectionY + rowHeight / 2 + 1, { align: 'center' });
        doc.line(tableX, sectionY, tableX + totalWidth, sectionY);
        doc.line(tableX + colWidths[0], sectionY, tableX + colWidths[0], sectionY + rowHeight);
        doc.line(tableX, sectionY + rowHeight, tableX + totalWidth, sectionY + rowHeight);

        sectionY += rowHeight;
        doc.setFont(undefined, 'normal');

        section.rows.forEach((row, idx) => {
          if (sectionY > 270) {
            doc.addPage();
            sectionY = 20;
          }

          if (idx % 2 === 0) {
            doc.setFillColor(255, 255, 255);
          } else {
            doc.setFillColor(250, 250, 250);
          }
          doc.rect(tableX, sectionY, totalWidth, rowHeight, 'F');

          doc.setFont(undefined, 'bold');
          doc.text(row[0], tableX + 5, sectionY + rowHeight / 2 + 1);
          doc.setFont(undefined, 'normal');
          doc.text(row[1], tableX + colWidths[0] + colWidths[1] / 2, sectionY + rowHeight / 2 + 1, { align: 'center' });

          doc.line(tableX, sectionY, tableX + totalWidth, sectionY);
          doc.line(tableX + colWidths[0], sectionY, tableX + colWidths[0], sectionY + rowHeight);
          doc.line(tableX, sectionY + rowHeight, tableX + totalWidth, sectionY + rowHeight);

          sectionY += rowHeight;
        });

        yPos = sectionY + 10;
      });
    } else {
      // Multiple records
      doc.setFontSize(20);
      doc.setTextColor(15, 43, 87);
      doc.text('Daily Business Records Report', 148.5, yPos, { align: 'center' });
      yPos += 15;

      doc.setFontSize(12);
      doc.setTextColor(100, 100, 100);
      doc.text(`Generated on: ${new Date().toLocaleDateString()}`, 148.5, yPos, { align: 'center' });
      yPos += 20;

      const tableX = 10;
      const colWidths = [30, 33, 35, 30, 30, 35, 54, 30];
      const rowHeight = 9;
      const totalWidth = colWidths.reduce((a, b) => a + b, 0);
      const headers = [
        'Date', 
        'Open Bal', 
        'Business Inc', 
        'Expenses', 
        'Net Profit', 
        'Closing Bal', 
        'Cash Handover', 
        'Next Day Open'
      ];

      doc.setLineWidth(0.2);
      doc.setDrawColor(180, 180, 180);
      doc.setFillColor(248, 250, 252);

      let currentY = yPos;

      function drawHeader() {
        doc.rect(tableX, currentY, totalWidth, rowHeight, 'F');
        doc.setFont(undefined, 'bold');
        doc.setFontSize(8);
        doc.setTextColor(100, 100, 100);
        let xPos = tableX;
        headers.forEach((header, i) => {
          doc.text(header, xPos + colWidths[i] / 2, currentY + rowHeight / 2 + 1, { align: 'center' });
          xPos += colWidths[i];
        });
        doc.setDrawColor(200, 200, 200);
        doc.line(tableX, currentY + rowHeight, tableX + totalWidth, currentY + rowHeight);
        xPos = tableX;
        colWidths.forEach(w => {
          doc.line(xPos, currentY, xPos, currentY + rowHeight);
          xPos += w;
        });
        doc.line(xPos, currentY, xPos, currentY + rowHeight);
        currentY += rowHeight;
      }

      drawHeader();

      doc.setFont(undefined, 'normal');
      doc.setFontSize(8);
      doc.setTextColor(0, 0, 0);

      data.forEach((record, index) => {
        if (currentY > 180) {
          doc.line(tableX, currentY, tableX + totalWidth, currentY);
          doc.addPage();
          currentY = 20;
          drawHeader();
        }

        if (index % 2 === 0) {
          doc.setFillColor(255, 255, 255);
        } else {
          doc.setFillColor(250, 250, 250);
        }
        doc.rect(tableX, currentY, totalWidth, rowHeight, 'F');

        const businessIncome = (parseFloat(record.cash || 0) + parseFloat(record.gpay || 0) + parseFloat(record.debit_card || 0) + parseFloat(record.credit_card || 0));
        const rowData = [
          formatDate(record.business_date),
          formatAmountForPdf(record.opening_balance),
          formatAmountForPdf(businessIncome),
          formatAmountForPdf(record.total_expenses),
          formatAmountForPdf(record.net_profit),
          formatAmountForPdf(record.closing_balance),
          formatAmountForPdf(record.cash_received),
          formatAmountForPdf((parseFloat(record.closing_balance || 0) - parseFloat(record.cash_received || 0)))
        ];
        let xPos = tableX;
        rowData.forEach((text, i) => {
          doc.text(text, xPos + colWidths[i] / 2, currentY + rowHeight / 2 + 1, { align: 'center' });
          xPos += colWidths[i];
        });

        doc.line(tableX, currentY + rowHeight, tableX + totalWidth, currentY + rowHeight);
        xPos = tableX;
        colWidths.forEach(w => {
          doc.line(xPos, currentY, xPos, currentY + rowHeight);
          xPos += w;
        });
        doc.line(xPos, currentY, xPos, currentY + rowHeight);

        currentY += rowHeight;
      });

      doc.line(tableX, currentY, tableX + totalWidth, currentY);
    }

    doc.save(filename);
  };

  const handleDownloadPDF = (record) => {
    generatePDF(record, `Business_Record_${record.business_date.replace(/-/g, '_')}.pdf`);
  };

  const handleExportAll = () => {
    if (filteredRecords.length === 0) {
      toast.error('No records to export');
      return;
    }
    const sortedForExport = [...filteredRecords].sort((a, b) => new Date(a.business_date) - new Date(b.business_date));
    generatePDF(sortedForExport, 'All_Business_Records.pdf');
  };

  const totals = calculateTotals();
  const viewingExpenses = viewingRecord ? buildExpensesFromRecord(viewingRecord) : [];
  const fieldInputClass = 'w-full px-3 sm:px-4 py-2 sm:py-2.5 text-sm rounded-xl border border-slate-200 bg-white dark:bg-slate-800 shadow-sm focus:outline-none focus:ring-2 focus:ring-primary/15 focus:border-primary hover:border-slate-300 transition-all font-semibold text-slate-800 placeholder:text-slate-400 placeholder:font-normal';

  return (
    <div className="max-w-7xl mx-auto px-2 sm:px-3 md:px-4 py-2 sm:py-3">
      {/* Header */}
      {/* Sticky UI Header Wrapper */}
      <div className="sticky top-0 z-10 bg-white dark:bg-slate-900 shadow-sm p-4 -mx-2 sm:-mx-3 md:-mx-4 mb-4 flex flex-col md:flex-row justify-between items-start md:items-center gap-3 rounded-b-2xl border-b border-slate-200 dark:border-slate-700">
        <div className="flex items-center gap-3 sm:gap-4">
          <div className="w-9 h-9 rounded-lg bg-gradient-to-tr from-primary to-accent flex items-center justify-center text-white shadow-md shadow-primary/20 shrink-0">
            <i className="fas fa-business-time text-sm"></i>
          </div>
          <div>
            <h1 className="text-lg font-bold text-slate-800 dark:text-slate-100 flex items-center gap-2">
              Daily Business Tracker
            </h1>
            <p className="text-[11px] mt-0.5 text-slate-500 dark:text-slate-400">
              Track your daily income and expenses with advanced analytics
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3 flex-wrap w-full md:w-auto shrink-0 mt-1 md:mt-0">
          {showForm ? (
            <div className="flex items-center gap-2 bg-gradient-to-r from-primary/5 to-accent/5 border border-primary/10 rounded-xl p-1.5 w-full md:w-auto justify-between md:justify-start">
              <label className="text-xs sm:text-sm font-semibold text-slate-600 dark:text-slate-400 whitespace-nowrap flex items-center gap-1">
                <i className="fas fa-calendar-alt text-primary text-xs"></i> <span>Entry Date:</span>
              </label>
              <input
                type="date"
                name="business_date"
                value={formData.business_date}
                onChange={handleInputChange}
                readOnly={!!editingRecord}
                min={!editingRecord && latestDate ? (() => {
                  const nextDate = new Date(latestDate);
                  nextDate.setDate(nextDate.getDate() + 1);
                  return nextDate.toISOString().split('T')[0];
                })() : undefined}
                max={!editingRecord && latestDate ? (() => {
                  const nextDate = new Date(latestDate);
                  nextDate.setDate(nextDate.getDate() + 1);
                  return nextDate.toISOString().split('T')[0];
                })() : undefined}
                required
                className={`px-2 py-1 text-xs sm:text-sm rounded-lg border border-slate-200 dark:border-slate-700 shadow-sm ${editingRecord ? 'bg-slate-100 dark:bg-slate-800/50 cursor-not-allowed text-slate-500' : 'bg-white dark:bg-slate-800'} focus:outline-none focus:ring-2 focus:ring-primary/15 focus:border-primary transition-all font-semibold text-slate-800 dark:text-slate-100`}
              />
            </div>
          ) : (
            hasPermission('daily_business', 'create') && (
              <Button
                fullWidth
                className="sm:w-auto"
                icon="fa-plus"
                onClick={() => {
                  // Set default date to next consecutive date when opening form
                  if (latestDate) {
                    const nextDate = new Date(latestDate);
                    nextDate.setDate(nextDate.getDate() + 1);
                    setFormData(prev => ({
                      ...prev,
                      business_date: nextDate.toISOString().split('T')[0]
                    }));
                  }
                  setShowForm(true);
                }}
              >
                New Entry
              </Button>
            )
          )}
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 sm:gap-2.5 mb-3 md:mb-4">
        <Card hover padding="none" title="Excludes opening balance" className="relative overflow-hidden border-l-4 border-l-emerald-500 bg-white dark:bg-slate-800/70 backdrop-blur-sm p-2 sm:p-2.5">
          <div className="flex justify-between items-center gap-2">
            <div className="min-w-0">
              <p className="text-[9px] sm:text-[10px] md:text-xs font-semibold text-slate-500 uppercase tracking-wider truncate">Total Income</p>
              <h3 className="text-sm sm:text-base md:text-lg font-bold text-emerald-600 dark:text-emerald-400 mt-0.5 truncate">{formatRupee(summary.totalIncome)}</h3>
            </div>
            <div className="w-7 h-7 rounded-lg bg-emerald-50 dark:bg-emerald-500/10 hidden sm:flex items-center justify-center text-emerald-500 shrink-0">
              <i className="fas fa-arrow-trend-up text-[10px] sm:text-xs"></i>
            </div>
          </div>
        </Card>

        <Card hover padding="none" title="All recorded expenses" className="relative overflow-hidden border-l-4 border-l-rose-500 bg-white dark:bg-slate-800/70 backdrop-blur-sm p-2 sm:p-2.5">
          <div className="flex justify-between items-center gap-2">
            <div className="min-w-0">
              <p className="text-[9px] sm:text-[10px] md:text-xs font-semibold text-slate-500 uppercase tracking-wider truncate">Total Expenses</p>
              <h3 className="text-sm sm:text-base md:text-lg font-bold text-rose-600 dark:text-rose-400 mt-0.5 truncate">{formatRupee(summary.totalExpenses)}</h3>
            </div>
            <div className="w-7 h-7 rounded-lg bg-rose-50 dark:bg-rose-500/10 hidden sm:flex items-center justify-center text-rose-500 shrink-0">
              <i className="fas fa-arrow-trend-down text-[10px] sm:text-xs"></i>
            </div>
          </div>
        </Card>

        <Card hover padding="none" title="Income minus expenses" className="relative overflow-hidden border-l-4 border-l-blue-500 bg-white dark:bg-slate-800/70 backdrop-blur-sm p-2 sm:p-2.5">
          <div className="flex justify-between items-center gap-2">
            <div className="min-w-0">
              <p className="text-[9px] sm:text-[10px] md:text-xs font-semibold text-slate-500 uppercase tracking-wider truncate">Net Profit</p>
              <h3 className="text-sm sm:text-base md:text-lg font-bold text-blue-600 dark:text-blue-400 mt-0.5 truncate">{formatRupee(summary.netProfit)}</h3>
            </div>
            <div className="w-7 h-7 rounded-lg bg-blue-50 dark:bg-blue-500/10 hidden sm:flex items-center justify-center text-blue-500 shrink-0">
              <i className="fas fa-chart-line text-[10px] sm:text-xs"></i>
            </div>
          </div>
        </Card>

        <Card hover padding="none" title="Final cash in store" className="relative overflow-hidden border-l-4 border-l-indigo-500 bg-white dark:bg-slate-800/70 backdrop-blur-sm p-2 sm:p-2.5">
          <div className="flex justify-between items-center gap-2">
            <div className="min-w-0">
              <p className="text-[9px] sm:text-[10px] md:text-xs font-semibold text-slate-500 uppercase tracking-wider truncate">Closing Balance</p>
              <h3 className="text-sm sm:text-base md:text-lg font-bold text-indigo-600 dark:text-indigo-400 mt-0.5 truncate">{formatRupee(summary.closingBalance)}</h3>
            </div>
            <div className="w-7 h-7 rounded-lg bg-indigo-50 dark:bg-indigo-500/10 hidden sm:flex items-center justify-center text-indigo-500 shrink-0">
              <i className="fas fa-wallet text-[10px] sm:text-xs"></i>
            </div>
          </div>
        </Card>
      </div>

      {/* Business Form */}
      {showForm && (hasPermission('daily_business', 'create') || hasPermission('daily_business', 'edit')) && (
        <Card padding="none" className="!rounded-2xl sm:!rounded-3xl p-4 sm:p-6 md:p-8 mb-4 sm:mb-6 md:mb-8 slide-in">
          <div className="flex justify-between items-center mb-4 sm:mb-6">
            <h4 className="text-lg sm:text-xl md:text-2xl font-bold gradient-text flex items-center gap-2 sm:gap-3">
              <span className="w-8 h-8 sm:w-10 sm:h-10 bg-gradient-to-br from-primary to-accent rounded-xl flex items-center justify-center shrink-0 shadow-md shadow-primary/20">
                <i className="fas fa-edit text-white text-sm sm:text-base" aria-hidden="true"></i>
              </span>
              <span>{editingRecord ? 'Edit Daily Business Entry' : 'Daily Business Entry'}</span>
            </h4>
            <button
              onClick={resetForm}
              className="w-10 h-10 rounded-xl bg-gray-100 dark:bg-slate-700 hover:bg-gray-200 dark:hover:bg-slate-600 text-gray-600 dark:text-slate-300 hover:text-gray-800 dark:hover:text-white transition-all duration-300 flex items-center justify-center btn-hover-scale shrink-0"
              aria-label="Close form"
            >
              <i className="fas fa-times text-sm" aria-hidden="true"></i>
            </button>
          </div>

          <form onSubmit={handleSubmit}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-5 md:gap-6">
              {/* Date Field */}
              <div className="lg:col-span-2 bg-gradient-to-r from-primary/5 to-accent/5 border border-primary/10 rounded-2xl p-3 sm:p-4">
                <label className="block text-xs sm:text-sm font-semibold text-slate-600 mb-1.5 sm:mb-2 flex items-center gap-1.5">
                  <i className="fas fa-calendar-alt text-primary text-xs"></i> <span>Entry Date</span>
                </label>
                <input
                  type="date"
                  name="business_date"
                  value={formData.business_date}
                  onChange={handleInputChange}
                  readOnly={!!editingRecord}
                  min={!editingRecord && latestDate ? (() => {
                    const nextDate = new Date(latestDate);
                    nextDate.setDate(nextDate.getDate() + 1);
                    return nextDate.toISOString().split('T')[0];
                  })() : undefined}
                  max={!editingRecord && latestDate ? (() => {
                    const nextDate = new Date(latestDate);
                    nextDate.setDate(nextDate.getDate() + 1);
                    return nextDate.toISOString().split('T')[0];
                  })() : undefined}
                  required
                  className={`w-full sm:max-w-xs px-3 sm:px-4 py-2 sm:py-2.5 text-sm rounded-xl border border-slate-200 shadow-sm ${editingRecord ? 'bg-slate-100 cursor-not-allowed text-slate-500' : 'bg-white dark:bg-slate-800'} focus:outline-none focus:ring-2 focus:ring-primary/15 focus:border-primary transition-all font-semibold text-slate-800`}
                />
                {!editingRecord && latestDate && (() => {
                  const nextDate = new Date(latestDate);
                  nextDate.setDate(nextDate.getDate() + 1);
                  return (
                    <p className="text-xs text-slate-500 mt-2 flex items-center gap-1.5">
                      <i className="fas fa-info-circle text-primary/70"></i> Dates must be consecutive. Last entry: {new Date(latestDate).toLocaleDateString()}. Next date: {nextDate.toLocaleDateString()}
                    </p>
                  );
                })()}
              </div>

              {/* Income Section */}
              <div className="space-y-3 sm:space-y-4">
                <div className="bg-gradient-to-br from-emerald-50 to-white border border-emerald-100 rounded-2xl p-4 sm:p-5 md:p-6 shadow-sm">
                  <h5 className="text-sm sm:text-base font-bold text-emerald-700 dark:text-emerald-400 mb-4 flex items-center gap-2.5">
                    <span className="w-8 h-8 rounded-xl bg-gradient-to-br from-emerald-500 to-emerald-600 flex items-center justify-center shadow-md shadow-emerald-500/20 shrink-0">
                      <i className="fas fa-money-bill-wave text-white text-xs sm:text-sm"></i>
                    </span>
                    Business Income
                  </h5>
                  <div className="space-y-3 sm:space-y-4">
                    <div>
                      <label className="block text-xs sm:text-sm font-semibold text-slate-600 mb-1 sm:mb-1.5 flex items-center gap-1.5">
                        <i className="fas fa-wallet text-emerald-500 text-xs"></i> <span>Opening balance (cash available in store)</span>
                      </label>
                      <input
                        type="number"
                        name="opening_balance"
                        value={formData.opening_balance}
                        onChange={handleInputChange}
                        onWheel={handleNumberInputWheel}
                        onKeyDown={handleNumberInputKeyDown}
                        min="0"
                        step="1"
                        placeholder="0"
                        readOnly={!!editingRecord || openingBalanceMeta.autoApplied}
                        className={`${fieldInputClass} ${(editingRecord || openingBalanceMeta.autoApplied) ? '!bg-slate-100 !text-slate-500 cursor-not-allowed' : ''}`}
                      />
                      {openingBalanceMeta.loading && (
                        <p className="text-xs text-slate-500 mt-1.5">Fetching opening balance...</p>
                      )}
                      {!openingBalanceMeta.loading && openingBalanceMeta.autoApplied && openingBalanceMeta.sourceDate && (
                        <p className="text-xs text-emerald-600 dark:text-emerald-400 mt-1.5 font-medium">
                          Auto-filled using closing balance from {formatDate(openingBalanceMeta.sourceDate)}
                        </p>
                      )}
                      {!openingBalanceMeta.loading && !openingBalanceMeta.autoApplied && !openingBalanceMeta.error && (
                        <p className="text-xs text-slate-400 mt-1.5">Enter opening balance for the first record.</p>
                      )}
                      {openingBalanceMeta.error && (
                        <p className="text-xs text-red-500 dark:text-red-400 mt-1.5">{openingBalanceMeta.error}</p>
                      )}
                    </div>
                    <div>
                      <label className="block text-xs sm:text-sm font-semibold text-slate-600 mb-1 sm:mb-1.5 flex items-center gap-1.5">
                        <i className="fas fa-money-bill text-emerald-500 text-xs"></i> <span>Cash Sales</span>
                      </label>
                      <input
                        type="number"
                        name="cash"
                        value={formData.cash}
                        onChange={handleInputChange}
                        onWheel={handleNumberInputWheel}
                        onKeyDown={handleNumberInputKeyDown}
                        min="0"
                        step="1"
                        placeholder="0"
                        className={fieldInputClass}
                      />
                    </div>
                    <div>
                      <label className="block text-xs sm:text-sm font-semibold text-slate-600 mb-1 sm:mb-1.5 flex items-center gap-1.5">
                        <i className="fas fa-mobile-alt text-emerald-500 text-xs"></i> <span>GPay Transactions</span>
                      </label>
                      <input
                        type="number"
                        name="gpay"
                        value={formData.gpay}
                        onChange={handleInputChange}
                        onWheel={handleNumberInputWheel}
                        onKeyDown={handleNumberInputKeyDown}
                        min="0"
                        step="1"
                        placeholder="0"
                        className={fieldInputClass}
                      />
                    </div>
                    <div>
                      <label className="block text-xs sm:text-sm font-semibold text-slate-600 mb-1 sm:mb-1.5 flex items-center gap-1.5">
                        <i className="fas fa-credit-card text-emerald-500 text-xs"></i> <span>Debit Card Payments</span>
                      </label>
                      <input
                        type="number"
                        name="debit_card"
                        value={formData.debit_card}
                        onChange={handleInputChange}
                        onWheel={handleNumberInputWheel}
                        onKeyDown={handleNumberInputKeyDown}
                        min="0"
                        step="1"
                        placeholder="0"
                        className={fieldInputClass}
                      />
                    </div>
                    <div>
                      <label className="block text-xs sm:text-sm font-semibold text-slate-600 mb-1 sm:mb-1.5 flex items-center gap-1.5">
                        <i className="fas fa-credit-card text-emerald-500 text-xs"></i> <span>Credit Card Payments</span>
                      </label>
                      <input
                        type="number"
                        name="credit_card"
                        value={formData.credit_card}
                        onChange={handleInputChange}
                        onWheel={handleNumberInputWheel}
                        onKeyDown={handleNumberInputKeyDown}
                        min="0"
                        step="1"
                        placeholder="0"
                        className={fieldInputClass}
                      />
                    </div>
                  </div>
                </div>
                <div className="bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-2xl p-4 sm:p-5 shadow-lg shadow-emerald-500/20">
                  <div className="flex flex-col gap-1.5">
                    <div className="flex justify-between items-center gap-3">
                      <span className="text-xs sm:text-sm font-semibold text-white/90">Total Business Income</span>
                      <span className="text-lg sm:text-xl font-bold text-white">{formatRupee(totals.businessIncome)}</span>
                    </div>
                    <div className="flex justify-between items-center gap-3 text-[11px] text-white/70">
                      <span>without opening balance</span>
                      <span>{formatRupee(totals.totalIncome)} with opening balance</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Expenses Section */}
              <div className="space-y-3 sm:space-y-4">
                <div className="bg-gradient-to-br from-rose-50 to-white border border-rose-100 rounded-2xl p-4 sm:p-5 md:p-6 shadow-sm">
                  <h5 className="text-sm sm:text-base font-bold text-rose-700 mb-4 flex items-center gap-2.5">
                    <span className="w-8 h-8 rounded-xl bg-gradient-to-br from-rose-500 to-red-600 flex items-center justify-center shadow-md shadow-rose-500/20 shrink-0">
                      <i className="fas fa-receipt text-white text-xs sm:text-sm"></i>
                    </span>
                    Business Expenses
                  </h5>
                  <div className="space-y-3 sm:space-y-4">
                    {expenses.map((expense, idx) => (
                      <div key={idx} className="grid grid-cols-12 gap-2 sm:gap-3 items-start">
                        <div className="col-span-7 sm:col-span-7">
                          <label className="block text-xs sm:text-sm font-semibold text-slate-600 mb-1 sm:mb-1.5 flex items-center gap-1.5">
                            <i className="fas fa-clipboard-list text-rose-500 text-xs"></i> <span>Reason</span>
                          </label>
                          <input
                            type="text"
                            value={expense.reason}
                            onChange={(e) => handleExpenseChange(idx, 'reason', e.target.value)}
                            placeholder="Enter reason"
                            maxLength={200}
                            className={fieldInputClass}
                          />
                        </div>
                        <div className="col-span-5 sm:col-span-4">
                          <label className="block text-xs sm:text-sm font-semibold text-slate-600 mb-1 sm:mb-1.5 flex items-center gap-1.5">
                            <i className="fas fa-rupee-sign text-rose-500 text-xs"></i> <span>Amount</span>
                          </label>
                          <input
                            type="number"
                            min="0"
                            step="1"
                            value={expense.amount}
                            onChange={(e) => handleExpenseChange(idx, 'amount', e.target.value)}
                            onWheel={handleNumberInputWheel}
                            onKeyDown={handleNumberInputKeyDown}
                            placeholder="0"
                            className={`${fieldInputClass} text-right`}
                          />
                        </div>
                        <div className="col-span-12 sm:col-span-1 flex sm:justify-center justify-end pt-6">
                          {idx > 1 && (
                            <button
                              type="button"
                              onClick={() => removeExpenseRow(idx)}
                              className="h-10 w-10 flex items-center justify-center rounded-xl border border-rose-200 text-rose-500 bg-white dark:bg-slate-800 hover:bg-rose-50 hover:border-rose-300 transition-all shadow-sm"
                              aria-label="Remove expense row"
                            >
                              <i className="fas fa-trash-alt text-xs"></i>
                            </button>
                          )}
                        </div>
                      </div>
                    ))}
                    <button
                      type="button"
                      disabled={expenses.length >= 20}
                      onClick={addExpenseRow}
                      className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-4 py-2.5 text-xs sm:text-sm font-semibold text-primary bg-white dark:bg-slate-800 border-2 border-primary/20 rounded-xl hover:bg-primary/5 hover:border-primary/40 transition-all shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <i className="fas fa-plus text-xs"></i> <span>Add Expense</span>
                    </button>
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5 sm:gap-3">
                  <div className="bg-gradient-to-br from-rose-500 to-red-600 rounded-2xl p-3.5 sm:p-4 shadow-lg shadow-rose-500/20">
                    <span className="text-[11px] font-semibold text-white/80 uppercase tracking-wide block">Total Expenses</span>
                    <span className="text-lg sm:text-xl font-bold text-white">{formatRupee(totals.totalExpenses)}</span>
                  </div>
                  <div className="bg-gradient-to-br from-blue-500 to-indigo-600 rounded-2xl p-3.5 sm:p-4 shadow-lg shadow-blue-500/20">
                    <span className="text-[11px] font-semibold text-white/80 uppercase tracking-wide block">Net Profit</span>
                    <span className="text-lg sm:text-xl font-bold text-white">{formatRupee(totals.netProfit)}</span>
                  </div>
                </div>
                <div className="bg-gradient-to-br from-purple-500 to-indigo-600 rounded-2xl p-4 shadow-lg shadow-purple-500/20">
                  <div className="flex justify-between items-center gap-3">
                    <span className="text-xs sm:text-sm font-semibold text-white/90">Closing Balance <span className="opacity-80 font-normal text-[10px] sm:text-xs ml-1">(Cash Available in Store)</span></span>
                    <span className="text-lg sm:text-xl font-bold text-white">{formatRupee(totals.closing)}</span>
                  </div>
                  <span className="text-[11px] text-white/70 block text-right mt-0.5">Opening balance + Cash sales - Total Expenses</span>
                </div>

                {/* Cash Received Input */}
                <div className="bg-gradient-to-br from-amber-50 to-white border border-amber-200 dark:border-amber-500/30 rounded-2xl p-4">
                  <label className="block text-xs sm:text-sm font-semibold text-slate-600 mb-1.5 flex items-center gap-1.5">
                    <i className="fas fa-hand-holding-usd text-amber-500 text-xs"></i> <span>Cash Handover</span>
                  </label>
                  <input
                    type="number"
                    name="cash_received"
                    value={formData.cash_received}
                    onChange={handleInputChange}
                    onWheel={handleNumberInputWheel}
                    onKeyDown={handleNumberInputKeyDown}
                    min="0"
                    step="1"
                    placeholder="0"
                    className={`${fieldInputClass} focus:ring-amber-500/15 focus:border-amber-500`}
                  />
                </div>

                {/* Opening Balance for Next Day */}
                <div className="bg-gradient-to-br from-green-500 to-emerald-600 rounded-2xl p-4 shadow-lg shadow-green-500/20">
                  <div className="flex justify-between items-center gap-3">
                    <span className="text-xs sm:text-sm font-semibold text-white/90">Opening balance for next day</span>
                    <span className="text-lg sm:text-xl font-bold text-white">{formatRupee(totals.nextDayOpening)}</span>
                  </div>
                  <span className="text-[11px] text-white/70 block text-right mt-0.5">Closing balance - Cash Handover</span>
                </div>
              </div>

              {/* Submit and Cancel Buttons */}
              <div className="lg:col-span-2 flex flex-col sm:flex-row gap-2 sm:gap-3 md:gap-4 pt-2 border-t border-slate-100">
                <Button type="button" variant="secondary" fullWidth className="sm:flex-1" icon="fa-times" onClick={resetForm}>
                  Cancel
                </Button>
                {(editingRecord ? hasPermission('daily_business', 'edit') : hasPermission('daily_business', 'create')) && (
                  <Button type="submit" fullWidth className="sm:flex-1" icon="fa-check">
                    {editingRecord ? 'Update' : 'Submit'}
                  </Button>
                )}
              </div>
            </div>
          </form>
        </Card>
      )}

      {/* Search with Filter Button */}
      {!showForm && (
        <div className="mb-3 sm:mb-4 md:mb-6">
          <Button fullWidth className="sm:w-auto" icon={showFilters ? 'fa-times' : 'fa-search'} onClick={() => setShowFilters(!showFilters)}>
            {showFilters ? 'Hide Filters' : 'Search with Filter'}
          </Button>
        </div>
      )}

      {/* Filters */}
      {showFilters && !showForm && (
        <Card className="mb-6 md:mb-8">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5 mb-5">
          <div className="flex flex-col space-y-2">
            <label htmlFor="filter-start-date" className="block text-sm font-semibold text-gray-500 dark:text-slate-400 flex items-center gap-2">
              <i className="fas fa-calendar-alt text-sm" aria-hidden="true"></i> <span>Start Date</span>
            </label>
            <input
              id="filter-start-date"
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="w-full px-4 py-3 text-sm rounded-lg border border-gray-200 dark:border-slate-700 bg-gray-50 dark:bg-slate-700/50 focus:outline-none focus:ring-2 focus:ring-primary transition-all"
            />
          </div>
          <div className="flex flex-col space-y-2">
            <label htmlFor="filter-end-date" className="block text-sm font-semibold text-gray-500 dark:text-slate-400 flex items-center gap-2">
              <i className="fas fa-calendar-alt text-sm" aria-hidden="true"></i> <span>End Date</span>
            </label>
            <input
              id="filter-end-date"
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="w-full px-4 py-3 text-sm rounded-lg border border-gray-200 dark:border-slate-700 bg-gray-50 dark:bg-slate-700/50 focus:outline-none focus:ring-2 focus:ring-primary transition-all"
            />
          </div>
          <div className="sm:col-span-2 lg:col-span-1 flex flex-col space-y-2">
            <label htmlFor="filter-search" className="block text-sm font-semibold text-gray-500 dark:text-slate-400 flex items-center gap-2">
              <i className="fas fa-search text-sm" aria-hidden="true"></i> <span>Search Records</span>
            </label>
            <input
              id="filter-search"
              type="search"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search by date, amount..."
              className="w-full px-4 py-3 text-sm rounded-lg border border-gray-200 dark:border-slate-700 bg-gray-50 dark:bg-slate-700/50 focus:outline-none focus:ring-2 focus:ring-primary transition-all"
            />
          </div>
        </div>
        <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 md:gap-4">
          <Button variant="secondary" fullWidth className="sm:w-auto" icon="fa-filter" onClick={() => { setStartDate(''); setEndDate(''); setSearchTerm(''); }}>
            Clear Filters
          </Button>
          <Button fullWidth className="sm:w-auto" icon="fa-download" onClick={handleExportAll}>
            Export All as PDF
          </Button>
        </div>
        </Card>
      )}

      {/* Records Table */}
      <Card>
        <h4 className="text-lg md:text-xl font-semibold text-gray-800 dark:text-slate-100 mb-5 md:mb-6 flex items-center gap-2">
          <i className="fas fa-list" aria-hidden="true"></i> Daily Business Records
        </h4>
        {loading ? (
          <TableSkeleton rows={6} columns={8} />
        ) : filteredRecords.length === 0 ? (
          <div className="text-center py-8 sm:py-12">
            {rateLimited ? (
              <div className="bg-amber-50 dark:bg-amber-500/10 border-2 border-amber-400 dark:border-amber-500/40 rounded-lg sm:rounded-xl p-4 sm:p-6 max-w-md mx-auto">
                <i className="fas fa-exclamation-triangle text-amber-600 dark:text-amber-400 text-2xl sm:text-3xl md:text-4xl mb-3 sm:mb-4"></i>
                <h3 className="text-sm sm:text-base md:text-lg font-bold text-amber-800 dark:text-amber-300 mb-2">Rate Limit Exceeded</h3>
                <p className="text-xs sm:text-sm text-amber-700 dark:text-amber-400 mb-3 sm:mb-4">
                  Too many requests. Please wait a few minutes before refreshing the page.
                </p>
                <button
                  onClick={() => {
                    setRateLimited(false);
                    loadRecords();
                  }}
                  disabled={loadingRef.current}
                  className="px-3 sm:px-4 py-2 text-xs sm:text-sm bg-amber-600 text-white rounded-lg font-semibold hover:bg-amber-700 transition-colors flex items-center gap-1.5 mx-auto disabled:opacity-50"
                >
                  <i className="fas fa-redo text-xs" aria-hidden="true"></i> Retry After Waiting
                </button>
              </div>
            ) : (
              <div className="text-xs sm:text-sm text-slate-400 dark:text-slate-500 italic">
                No records found. Click "Create New Entry" to add your first record.
              </div>
            )}
          </div>
        ) : (
          <>
            {/* Desktop Table View */}
            <div className="hidden md:block overflow-x-auto -mx-4 sm:mx-0">
              <table className="w-full min-w-[800px]">
                <thead>
                  <tr className="bg-slate-50 dark:bg-slate-700/50">
                    <th className="px-2 sm:px-4 py-2 sm:py-3 text-left text-xs sm:text-sm font-semibold text-slate-500 dark:text-slate-400">S.No</th>
                    <th className="px-2 sm:px-4 py-2 sm:py-3 text-left text-xs sm:text-sm font-semibold text-slate-500 dark:text-slate-400">Date</th>
                    <th className="px-2 sm:px-4 py-2 sm:py-3 text-left text-xs sm:text-sm font-semibold text-slate-500 dark:text-slate-400">Opening Balance</th>
                    <th className="px-2 sm:px-4 py-2 sm:py-3 text-left text-xs sm:text-sm font-semibold text-slate-500 dark:text-slate-400">Business Income</th>
                    <th className="px-2 sm:px-4 py-2 sm:py-3 text-left text-xs sm:text-sm font-semibold text-slate-500 dark:text-slate-400">Expenses</th>
                    <th className="px-2 sm:px-4 py-2 sm:py-3 text-left text-xs sm:text-sm font-semibold text-slate-500 dark:text-slate-400">Net Profit</th>
                    <th className="px-2 sm:px-4 py-2 sm:py-3 text-left text-xs sm:text-sm font-semibold text-slate-500 dark:text-slate-400">Closing Balance<br/><span className="font-normal text-[10px] sm:text-xs">(Cash Available in Store)</span></th>
                    <th className="px-2 sm:px-4 py-2 sm:py-3 text-left text-xs sm:text-sm font-semibold text-slate-500 dark:text-slate-400">Cash Handover</th>
                    <th className="px-2 sm:px-4 py-2 sm:py-3 text-left text-xs sm:text-sm font-semibold text-slate-500 dark:text-slate-400">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedRecords.map((record, index) => {
                    const businessIncome = (parseFloat(record.cash || 0) + parseFloat(record.gpay || 0) + parseFloat(record.debit_card || 0) + parseFloat(record.credit_card || 0));
                    const serialNumber = startIndex + index + 1;
                    return (
                      <tr key={record.id} id={`record-${record.id}`} className="border-b border-gray-200 dark:border-slate-700 hover:bg-gray-50 dark:hover:bg-slate-800/60">
                        <td className="px-2 sm:px-4 py-2 sm:py-3 text-xs sm:text-sm">{serialNumber}</td>
                        <td className="px-2 sm:px-4 py-2 sm:py-3 text-xs sm:text-sm">{formatDate(record.business_date)}</td>
                        <td className="px-2 sm:px-4 py-2 sm:py-3 text-xs sm:text-sm">{formatRupee(record.opening_balance)}</td>
                        <td className="px-2 sm:px-4 py-2 sm:py-3 text-xs sm:text-sm">{formatRupee(businessIncome)}</td>
                        <td className="px-2 sm:px-4 py-2 sm:py-3 text-xs sm:text-sm">{formatRupee(record.total_expenses)}</td>
                        <td className="px-2 sm:px-4 py-2 sm:py-3 text-xs sm:text-sm">{formatRupee(record.net_profit)}</td>
                        <td className="px-2 sm:px-4 py-2 sm:py-3 text-xs sm:text-sm">{formatRupee(record.closing_balance)}</td>
                        <td className="px-2 sm:px-4 py-2 sm:py-3 text-xs sm:text-sm">{formatRupee(record.cash_received || 0)}</td>
                        <td className="px-2 sm:px-4 py-2 sm:py-3">
                          <div className="flex flex-wrap gap-1 sm:gap-2">
                            {hasPermission('daily_business', 'view') && (
                              <Button size="sm" variant="info" icon="fa-eye" onClick={() => handleView(record)} aria-label="View record">
                                <span className="hidden lg:inline">View</span>
                              </Button>
                            )}
                            {hasPermission('daily_business', 'edit') && (
                              <Button size="sm" variant="success" icon="fa-edit" onClick={() => handleEdit(record)} aria-label="Edit record">
                                <span className="hidden lg:inline">Edit</span>
                              </Button>
                            )}
                            {hasPermission('daily_business', 'delete') && (
                              <Button size="sm" variant="danger" icon="fa-trash" onClick={() => handleDelete(record.id)} aria-label="Delete record">
                                <span className="hidden lg:inline">Delete</span>
                              </Button>
                            )}
                            {hasPermission('daily_business', 'view') && (
                              <Button size="sm" variant="purple" icon="fa-download" onClick={() => handleDownloadPDF(record)} aria-label="Download PDF">
                                <span className="hidden lg:inline">PDF</span>
                              </Button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Mobile Card View */}
            <div className="md:hidden space-y-1.5">
              {paginatedRecords.map((record, index) => {
                const businessIncome = (parseFloat(record.cash || 0) + parseFloat(record.gpay || 0) + parseFloat(record.debit_card || 0) + parseFloat(record.credit_card || 0));
                const serialNumber = startIndex + index + 1;
                return (
                  <div key={record.id} id={`record-${record.id}`} className="bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-lg p-2 shadow-sm hover:shadow-md transition-shadow">
                    <div className="flex justify-between items-center mb-1.5 pb-1.5 border-b">
                      <div>
                        <span className="text-[8px] sm:text-[9px] text-gray-500 dark:text-slate-400">#{serialNumber}</span>
                        <h5 className="text-[11px] sm:text-xs font-bold text-gray-800 dark:text-slate-100">{formatDate(record.business_date)}</h5>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-1 mb-1.5">
                      <div className="bg-green-50 dark:bg-emerald-500/10 p-1 sm:p-1.5 rounded">
                        <div className="text-[8px] sm:text-[9px] text-green-600 dark:text-emerald-400 mb-0 flex items-center gap-0.5">
                          <i className="fas fa-arrow-up text-[7px]"></i> <span>Income</span>
                        </div>
                        <div className="text-[10px] sm:text-[11px] font-bold text-green-700 dark:text-emerald-400">{formatRupee(businessIncome)}</div>
                      </div>
                      <div className="bg-red-50 dark:bg-red-500/10 p-1 sm:p-1.5 rounded">
                        <div className="text-[8px] sm:text-[9px] text-red-600 dark:text-red-400 mb-0 flex items-center gap-0.5">
                          <i className="fas fa-arrow-down text-[7px]"></i> <span>Expenses</span>
                        </div>
                        <div className="text-[10px] sm:text-[11px] font-bold text-red-700 dark:text-red-400">{formatRupee(record.total_expenses)}</div>
                      </div>
                      <div className="bg-blue-50 dark:bg-blue-500/10 p-1 sm:p-1.5 rounded">
                        <div className="text-[8px] sm:text-[9px] text-blue-600 dark:text-blue-400 mb-0 flex items-center gap-0.5">
                          <i className="fas fa-chart-line text-[7px]"></i> <span>Profit</span>
                        </div>
                        <div className="text-[10px] sm:text-[11px] font-bold text-blue-700 dark:text-blue-300">{formatRupee(record.net_profit)}</div>
                      </div>
                      <div className="bg-purple-50 dark:bg-purple-500/10 p-1 sm:p-1.5 rounded">
                        <div className="text-[8px] sm:text-[9px] text-purple-600 dark:text-purple-400 mb-0 flex items-center gap-0.5">
                          <i className="fas fa-wallet text-[7px]"></i> <span>Closing</span>
                        </div>
                        <div className="text-[10px] sm:text-[11px] font-bold text-purple-700 dark:text-purple-300">{formatRupee(record.closing_balance)}</div>
                      </div>
                      <div className="bg-amber-50 dark:bg-amber-500/10 p-1 sm:p-1.5 rounded">
                        <div className="text-[8px] sm:text-[9px] text-amber-600 dark:text-amber-400 mb-0 flex items-center gap-0.5">
                          <i className="fas fa-hand-holding-usd text-[7px]"></i> <span>Cash to Owner</span>
                        </div>
                        <div className="text-[10px] sm:text-[11px] font-bold text-amber-700 dark:text-amber-300">{formatRupee(record.cash_received || 0)}</div>
                      </div>
                      <div className="bg-emerald-50 dark:bg-emerald-500/10 p-1 sm:p-1.5 rounded">
                        <div className="text-[8px] sm:text-[9px] text-emerald-600 dark:text-emerald-400 mb-0 flex items-center gap-0.5">
                          <i className="fas fa-calendar-plus text-[7px]"></i> <span>Next Day Open</span>
                        </div>
                        <div className="text-[10px] sm:text-[11px] font-bold text-emerald-700 dark:text-emerald-300">{formatRupee((parseFloat(record.closing_balance || 0) - parseFloat(record.cash_received || 0)))}</div>
                      </div>
                    </div>
                    <div className="flex flex-wrap gap-1 pt-1.5 border-t">
                      {hasPermission('daily_business', 'view') && (
                        <Button size="sm" variant="info" icon="fa-eye" onClick={() => handleView(record)} aria-label="View" className="!py-0.5 !px-1.5 !text-[9px] !rounded-md" />
                      )}
                      {hasPermission('daily_business', 'edit') && (
                        <Button size="sm" variant="success" icon="fa-edit" onClick={() => handleEdit(record)} aria-label="Edit" className="!py-0.5 !px-1.5 !text-[9px] !rounded-md" />
                      )}
                      {hasPermission('daily_business', 'delete') && (
                        <Button size="sm" variant="danger" icon="fa-trash" onClick={() => handleDelete(record.id)} aria-label="Delete" className="!py-0.5 !px-1.5 !text-[9px] !rounded-md" />
                      )}
                      {hasPermission('daily_business', 'view') && (
                        <Button size="sm" variant="purple" icon="fa-download" onClick={() => handleDownloadPDF(record)} aria-label="PDF" className="!py-0.5 !px-1.5 !text-[9px] !rounded-md" />
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
            
            {/* Pagination Controls */}
            {totalPages > 1 && (
              <div className="flex flex-col items-center gap-2 mt-4 sm:mt-6 p-3 sm:p-4 bg-gray-50 dark:bg-slate-700/50 rounded-lg sm:rounded-xl">
                <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={setCurrentPage} />
                <span className="text-xs font-semibold text-gray-500 dark:text-slate-400">Page {currentPage} of {totalPages}</span>
              </div>
            )}
          </>
        )}
      </Card>

      {/* Read-Only Record View Modal */}
      {viewingRecord && (
        <div 
          className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4 pt-[max(1rem,env(safe-area-inset-top))] pb-[max(1rem,env(safe-area-inset-bottom))] overflow-y-auto"
          onClick={closeViewModal}
          onKeyDown={(e) => {
            if (e.key === 'Escape') {
              closeViewModal();
            }
          }}
          tabIndex={-1}
          role="dialog"
          aria-modal="true"
          aria-labelledby="view-record-title"
        >
          <div
            className="bg-white dark:bg-slate-800 rounded-2xl sm:rounded-3xl p-6 sm:p-8 max-w-4xl w-full shadow-2xl my-8 max-h-dvh-90 overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex justify-between items-center mb-4 sm:mb-6">
              <h2 id="view-record-title" className="text-xl sm:text-2xl md:text-3xl font-bold gradient-text flex items-center gap-2 sm:gap-3">
                <div className="w-8 h-8 sm:w-10 sm:h-10 bg-gradient-to-br from-primary to-primary-dark rounded-xl flex items-center justify-center">
                  <i className="fas fa-clipboard-check text-white text-sm sm:text-base"></i>
                </div>
                Daily Business Record - Read Only
              </h2>
              <button
                onClick={closeViewModal}
                className="w-10 h-10 shrink-0 rounded-xl bg-gray-100 dark:bg-slate-700 hover:bg-gray-200 dark:hover:bg-slate-600 text-gray-600 dark:text-slate-300 hover:text-gray-800 dark:hover:text-white transition-all duration-300 flex items-center justify-center btn-hover-scale"
                aria-label="Close modal"
              >
                <i className="fas fa-times text-sm sm:text-base"></i>
              </button>
            </div>

            <div className="space-y-4 sm:space-y-6">
              {/* Date */}
              <div className="bg-gradient-to-r from-primary/10 to-accent/10 rounded-xl p-3 sm:p-4 border-2 border-primary/20">
                <label className="block text-xs sm:text-sm font-semibold text-gray-500 dark:text-slate-400 mb-2 flex items-center gap-2">
                  <i className="fas fa-calendar-alt text-xs sm:text-sm"></i> Business Date
                </label>
                <div className="text-base sm:text-lg font-bold text-gray-800 dark:text-slate-100">
                  {formatDate(viewingRecord.business_date)}
                </div>
              </div>

              {/* Opening Balance */}
              <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl p-4 border-2 border-green-200 dark:border-emerald-500/30">
                <label className="block text-sm font-semibold text-gray-500 dark:text-slate-400 mb-2 flex items-center gap-2">
                  <i className="fas fa-wallet"></i> Opening balance (cash available in store)
                </label>
                <div className="text-2xl font-bold text-green-700 dark:text-emerald-400">
                  {formatRupee(viewingRecord.opening_balance || '0')}
                </div>
              </div>

              {/* Business Income Section */}
              <div className="border-l-4 border-emerald-600 dark:border-emerald-500/50 bg-emerald-50 dark:bg-emerald-500/10 rounded-xl p-5">
                <h5 className="text-base font-semibold text-emerald-600 dark:text-emerald-400 mb-4 flex items-center gap-2">
                  <i className="fas fa-arrow-up"></i> Business Income
                </h5>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-gray-500 dark:text-slate-400 mb-2 flex items-center gap-2">
                      <i className="fas fa-money-bill-wave"></i> Cash
                    </label>
                    <div className="text-lg font-bold text-gray-800 dark:text-slate-100 bg-white dark:bg-slate-800 rounded-lg p-3 border border-gray-200 dark:border-slate-700">
                      {formatRupee(viewingRecord.cash || '0')}
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-500 dark:text-slate-400 mb-2 flex items-center gap-2">
                      <i className="fas fa-mobile-alt"></i> GPay
                    </label>
                    <div className="text-lg font-bold text-gray-800 dark:text-slate-100 bg-white dark:bg-slate-800 rounded-lg p-3 border border-gray-200 dark:border-slate-700">
                      {formatRupee(viewingRecord.gpay || '0')}
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-500 dark:text-slate-400 mb-2 flex items-center gap-2">
                      <i className="fas fa-credit-card"></i> Debit Card
                    </label>
                    <div className="text-lg font-bold text-gray-800 dark:text-slate-100 bg-white dark:bg-slate-800 rounded-lg p-3 border border-gray-200 dark:border-slate-700">
                      {formatRupee(viewingRecord.debit_card || '0')}
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-500 dark:text-slate-400 mb-2 flex items-center gap-2">
                      <i className="fas fa-credit-card"></i> Credit Card
                    </label>
                    <div className="text-lg font-bold text-gray-800 dark:text-slate-100 bg-white dark:bg-slate-800 rounded-lg p-3 border border-gray-200 dark:border-slate-700">
                      {formatRupee(viewingRecord.credit_card || '0')}
                    </div>
                  </div>
                </div>
                <div className="mt-4 bg-gradient-to-b from-emerald-50 to-emerald-100 dark:from-emerald-500/10 dark:to-emerald-500/5 border border-emerald-600/30 dark:border-emerald-500/30 rounded-xl p-4">
                  <div className="flex flex-col gap-1">
                    <div className="flex justify-between items-center">
                      <span className="text-sm font-semibold text-emerald-600 dark:text-emerald-400">Total Business Income</span>
                      <span className="text-lg font-bold text-emerald-600 dark:text-emerald-400">
                        {formatRupee((parseFloat(viewingRecord.cash || 0) + parseFloat(viewingRecord.gpay || 0) + parseFloat(viewingRecord.debit_card || 0) + parseFloat(viewingRecord.credit_card || 0)))} <span className="text-xs font-normal text-gray-600 dark:text-slate-300">(without opening balance)</span>
                      </span>
                    </div>
                    <div className="flex justify-end items-center">
                      <span className="text-xs font-normal text-gray-600 dark:text-slate-300">
                        {formatRupee((parseFloat(viewingRecord.opening_balance || 0) + parseFloat(viewingRecord.cash || 0) + parseFloat(viewingRecord.gpay || 0) + parseFloat(viewingRecord.debit_card || 0) + parseFloat(viewingRecord.credit_card || 0)))} <span className="text-xs">(with opening balance without expenses)</span>
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Expenses Section */}
              <div className="border-l-4 border-rose-500 dark:border-rose-500/50 bg-red-50 dark:bg-red-500/10 rounded-xl p-5">
                <h5 className="text-base font-semibold text-rose-500 dark:text-rose-400 mb-4 flex items-center gap-2">
                  <i className="fas fa-arrow-down"></i> Business Expenses
                </h5>
                <div className="space-y-2">
                  {viewingExpenses.length ? (
                    viewingExpenses.map((exp, idx) => (
                      <div key={idx} className="flex items-center justify-between bg-white dark:bg-slate-800 rounded-lg p-3 border border-gray-200 dark:border-slate-700">
                        <div className="text-sm font-semibold text-gray-700 dark:text-slate-300">{exp.reason || 'Expense'}</div>
                        <div className="text-lg font-bold text-gray-800 dark:text-slate-100">{formatRupee(exp.amount || 0)}</div>
                      </div>
                    ))
                  ) : (
                    <div className="text-sm text-gray-500 dark:text-slate-400">No expenses recorded</div>
                  )}
                </div>
                <div className="mt-4 bg-gradient-to-b from-rose-100 to-rose-200 dark:from-rose-500/10 dark:to-rose-500/5 border border-rose-500/30 dark:border-rose-500/30 rounded-xl p-4">
                  <div className="flex justify-between items-center">
                    <span className="text-sm font-semibold text-rose-500 dark:text-rose-400">Total Expenses</span>
                    <span className="text-lg font-bold text-rose-500 dark:text-rose-400">
                      {formatRupee(viewingRecord.total_expenses || viewingRecord.other_expenses || '0')}
                    </span>
                  </div>
                </div>
              </div>

              {/* Summary Section */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl p-5 border-2 border-blue-200">
                  <label className="block text-sm font-semibold text-gray-500 dark:text-slate-400 mb-2 flex items-center gap-2">
                    <i className="fas fa-chart-line"></i> Net Profit
                  </label>
                  <div className="text-2xl font-bold text-blue-700">
                    {formatRupee(viewingRecord.net_profit || '0')}
                  </div>
                </div>
                <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-xl p-5 border-2 border-purple-200">
                  <label className="block text-sm font-semibold text-gray-500 dark:text-slate-400 mb-2 flex items-center gap-2">
                    <i className="fas fa-wallet"></i> Closing Balance <span className="font-normal text-[10px] sm:text-xs ml-1">(Cash Available in Store)</span>
                  </label>
                  <div className="text-2xl font-bold text-purple-700">
                    {formatRupee(viewingRecord.closing_balance || '0')}
                  </div>
                  <div className="text-xs text-gray-600 dark:text-slate-300 mt-1">(Opening balance + Cash sales - Total Expenses)</div>
                </div>
              </div>
              
              {/* Cash Received */}
              {viewingRecord.cash_received !== undefined && viewingRecord.cash_received !== null && viewingRecord.cash_received !== '' && (
                <>
                  <div className="bg-gradient-to-r from-amber-50 to-yellow-50 rounded-xl p-4 border-2 border-amber-200 dark:border-amber-500/30">
                    <label className="block text-sm font-semibold text-gray-500 dark:text-slate-400 mb-2 flex items-center gap-2">
                      <i className="fas fa-hand-holding-usd"></i> Cash Handover
                    </label>
                    <div className="text-2xl font-bold text-amber-700 dark:text-amber-400">
                      {formatRupee(viewingRecord.cash_received || '0')}
                    </div>
                  </div>
                  
                  {/* Opening Balance for Next Day */}
                  <div className="bg-gradient-to-r from-green-100 to-emerald-100 rounded-xl p-4 border-2 border-green-300">
                    <label className="block text-sm font-semibold text-gray-500 dark:text-slate-400 mb-2 flex items-center gap-2">
                      <i className="fas fa-calendar-plus"></i> Opening balance (cash available in shop) for next day
                    </label>
                    <div className="text-2xl font-bold text-green-700 dark:text-emerald-400">
                      {formatRupee((parseFloat(viewingRecord.closing_balance || 0) - parseFloat(viewingRecord.cash_received || 0)))}
                    </div>
                    <div className="text-xs text-gray-600 dark:text-slate-300 mt-1">(Closing balance - Cash Handover)</div>
                  </div>
                </>
              )}
            </div>

            <div className="flex flex-col sm:flex-row justify-end gap-3 sm:gap-4 mt-6 sm:mt-8">
              <Button variant="secondary" fullWidth className="sm:w-auto" onClick={closeViewModal} aria-label="Close modal">
                Close
              </Button>
              {hasPermission('daily_business', 'view') && (
                <Button
                  variant="purple"
                  fullWidth
                  className="sm:w-auto"
                  icon="fa-download"
                  onClick={() => handleDownloadPDF(viewingRecord)}
                  aria-label="Download PDF"
                >
                  Download PDF
                </Button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DailyBusinessPage;
